/*
 * Copyright (c) 2026. Tooling Team
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Layout as AbyssLayout } from "@uhg-abyss/web/ui/Layout";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { Prompt, useHistory } from "react-router-dom";
import BulkEditModal from "./table/LabDomainCodeMapping/components/BulkEditModal";
import MappingHistoryDrawer from "./table/LabDomainCodeMapping/components/MappingHistoryDrawer";
import { LAB_DOMAIN_COLUMNS } from "./table/LabDomainCodeMapping/utils/columnConfig";
import {
  LabCodeEvidenceNavigationContext,
  LoincValuesContext,
} from "./table/LabDomainCodeMapping/utils/contexts";
import { saveLabMvdReturnState } from "./table/LabDomainCodeMapping/utils/navigationState";
import {
  compareDateColumnValues,
  createDownloadDropdownItems,
  formatDateOnlyFilterValue,
  MvdClearFiltersToolbar,
  useDefaultAdvancedFilterInput,
} from "./table/mvdDataTableUtils";
import {
  MAPPING_STATUS_OPTIONS,
  ML_PROBABILITY_COLUMN_KEY,
  PRODUCT_USAGE_COLUMN_KEY,
  PRODUCT_USAGE_FILTER_OPTIONS,
} from "./table/LabDomainCodeMapping/utils/constants";
import {
  compareMiProbability,
  formatCellValue,
  formatMiProbabilityValue,
  getRowsSortedByMiProbability,
} from "./table/LabDomainCodeMapping/utils/formatters";

const LAB_DOMAIN_ROW_ID = "__labDomainRowId";
const UPDATED_AT_COLUMN_KEY = "updatedAt";
const MIN_WIDTH_COLUMN_KEYS = ["loincConceptCode", "status"];
const STATUS_FILTER_OPTIONS = MAPPING_STATUS_OPTIONS.map(
  ({ label, value }) => ({
    label,
    value,
  })
);

const TABLE_CONTAINER_STYLE = {
  maxWidth: "100%",
  overflowX: "auto",
};

const HISTORY_LINK_STYLE = {
  background: "none",
  border: 0,
  color: "#002677",
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
};

const DISCLAIMER_STYLE = {
  backgroundColor: "#f7f9fc",
  border: "1px solid #d6deea",
  borderRadius: "4px",
  color: "#1f2937",
  marginBottom: "12px",
  padding: "12px 16px",
};

const DISCLAIMER_TITLE_STYLE = {
  display: "block",
  fontWeight: 700,
  marginBottom: "4px",
};

const getColumnKeys = () => Object.keys(LAB_DOMAIN_COLUMNS);

const formatColumnHeader = (key) => LAB_DOMAIN_COLUMNS[key].display;

const getDefaultSorting = (columnKey) =>
  columnKey ? [{ id: columnKey, desc: false }] : [];

const formatMvdTimestamp = (value) =>
  value ? moment(value).format("MM-DD-YYYY  HH:mm:ss") : "";

const getLoincConceptCode = (row) =>
  row?.loincConceptCode || row?.loincConcept?.code || "";

const getLoincConceptDescription = (row) =>
  row?.loincConceptDescription || row?.loincConcept?.description || "";

const hasPayloadValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const hasField = (row, field) =>
  row !== undefined &&
  row !== null &&
  Object.prototype.hasOwnProperty.call(row, field);

const getLabDomainRowId = (row, index) =>
  String(
    row?._id ??
      row?.id ??
      row?.uuid ??
      row?.lab_code ??
      row?.labCode ??
      row?.code ??
      index
  );

const getLabDomainRecordId = (row) =>
  row?._id ?? row?.[LAB_DOMAIN_ROW_ID] ?? row?.id ?? row?.uuid;

const getRowsWithInternalIds = (rows) =>
  rows.map((row, index) => ({
    ...row,
    [LAB_DOMAIN_ROW_ID]: getLabDomainRowId(row, index),
  }));

export default function LabMapValueDefinitionGrid({
  rowData,
  loincValues,
  isOpenEditDialog,
  onCloseEditDialog,
  onConfirmEdit,
  onOpenEditDialog,
  onSelectedRowsChange,
  onEditRowsButtonDisabledChange,
  onTableStateChange,
  onInlineEditingChange,
  getReturnState,
  initialTableState,
  mapStatus,
  isEditableVersion,
}) {
  const history = useHistory();
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [pendingCodeEvidenceNavigation, setPendingCodeEvidenceNavigation] =
    useState(null);
  const [sorting, setSorting] = useState(
    () =>
      initialTableState?.sorting || getDefaultSorting(ML_PROBABILITY_COLUMN_KEY)
  );
  const [columnFilters, setColumnFilters] = useState(
    () => initialTableState?.columnFilters || []
  );
  const [pagination, setPagination] = useState(
    () => initialTableState?.pagination || { pageIndex: 0, pageSize: 25 }
  );
  const loincValuesRef = useRef(loincValues);
  loincValuesRef.current = loincValues;
  const selectedRowCountRef = useRef(0);
  const allowPendingNavigationRef = useRef(false);
  const canEdit = mapStatus !== "REVIEW" && isEditableVersion !== false;

  const handleColumnFiltersChange = (updater) => {
    setColumnFilters(updater);
    setPagination((currentPagination) => ({
      ...currentPagination,
      pageIndex: 0,
    }));
  };

  const openMappingHistory = (row) => {
    setSelectedHistoryRow(row);
    setIsHistoryDrawerOpen(true);
  };

  const renderHistoryTimestampCell = (cell) => {
    const row = cell.row?.original;
    const value = formatCellValue(
      formatMvdTimestamp(row?.[UPDATED_AT_COLUMN_KEY])
    );
    if (!value) return "";

    return (
      <button
        onClick={() => openMappingHistory(row)}
        style={HISTORY_LINK_STYLE}
        type="button"
      >
        {value}
      </button>
    );
  };

  const handleEditCompleted = (previousRow, updatedRow) => {
    const changedFields = {};
    const previousLoincCode = getLoincConceptCode(previousRow);
    const updatedLoincCode = getLoincConceptCode(updatedRow);
    if (
      hasPayloadValue(updatedLoincCode) &&
      previousLoincCode !== updatedLoincCode
    ) {
      changedFields.loincCode = updatedLoincCode;
    }
    if (
      hasPayloadValue(updatedRow.status) &&
      previousRow.status !== updatedRow.status
    ) {
      changedFields.status = updatedRow.status;
    }
    if (
      hasField(updatedRow, "userComments") &&
      previousRow.userComments !== updatedRow.userComments
    ) {
      changedFields.userComment = updatedRow.userComments;
    }
    if (!Object.keys(changedFields).length) {
      return;
    }
    const recordId =
      getLabDomainRecordId(updatedRow) ?? getLabDomainRecordId(previousRow);
    if (!recordId) {
      return;
    }
    onConfirmEdit({
      body: [{ _id: recordId, ...changedFields }],
      ...(changedFields.loincCode !== undefined && {
        loincConceptDescription: getLoincConceptDescription(updatedRow),
      }),
    });
  };

  const sortedRows = useMemo(
    () => getRowsSortedByMiProbability(Array.isArray(rowData) ? rowData : []),
    [rowData]
  );
  const tableRows = useMemo(
    () => getRowsWithInternalIds(sortedRows),
    [sortedRows]
  );

  const columns = useMemo(() => {
    const labColumns = getColumnKeys()
      .filter((key) => key !== "loincConcept")
      .map((key) => ({
        id: key,
        header: formatColumnHeader(key),
        accessorKey: key,
        accessorFn:
          key === "loincConceptCode"
            ? getLoincConceptCode
            : key === "loincConceptDescription"
            ? getLoincConceptDescription
            : key === ML_PROBABILITY_COLUMN_KEY
            ? (row) => formatMiProbabilityValue(row?.[key])
            : (row) => row?.[key],
        ...(LAB_DOMAIN_COLUMNS[key].size !== undefined
          ? { size: LAB_DOMAIN_COLUMNS[key].size }
          : {}),
        ...(MIN_WIDTH_COLUMN_KEYS.includes(key) ? { minSize: 150 } : {}),
        ...(key === ML_PROBABILITY_COLUMN_KEY
          ? {
              sortDescFirst: false,
              sortingFn: (rowA, rowB) =>
                compareMiProbability(rowA.original, rowB.original),
            }
          : {}),
        cell: (cell) => {
          const Cell = LAB_DOMAIN_COLUMNS[key].Cell;
          return <Cell {...cell} />;
        },
      }));

    return [
      ...labColumns,
      {
        id: UPDATED_AT_COLUMN_KEY,
        header: "Updated At",
        accessorKey: UPDATED_AT_COLUMN_KEY,
        accessorFn: (row) =>
          formatDateOnlyFilterValue(row?.[UPDATED_AT_COLUMN_KEY]),
        sortingFn: (rowA, rowB) =>
          compareDateColumnValues(rowA, rowB, UPDATED_AT_COLUMN_KEY),
        cell: renderHistoryTimestampCell,
        meta: {
          headerLabel: "Updated At",
          customSetCellCsv: ({ value, original }) =>
            formatMvdTimestamp(original?.[UPDATED_AT_COLUMN_KEY] ?? value),
        },
      },
    ];
  }, []);

  const dataTableProps = useDataTable({
    initialData: tableRows,
    initialColumns: columns,
    rowIdKey: LAB_DOMAIN_ROW_ID,
    tableConfig: {
      enableColumnFilters: true,
      enableColumnResizing: true,
      columnResizeMode: "onEnd",
      enableSorting: true,
      enableRowSelection: true,
      onSortingChange: setSorting,
      state: {
        sorting,
        columnFilters,
        pagination,
      },
      onPaginationChange: setPagination,
      onColumnFiltersChange: handleColumnFiltersChange,
    },
    columnFilterConfig: {
      defaultSettings: {
        filterMode: "advanced",
      },
      additionalFilters: {
        productUsageIncludes: {
          filterFn: (row, columnId, filterValue) =>
            (row.getValue(columnId) ?? []).includes(filterValue),
          label: "Filter by Product Usage",
          inputCount: 1,
        },
      },
      individualSettings: {
        [PRODUCT_USAGE_COLUMN_KEY]: {
          filterMode: "advanced",
          inputConfig: {
            type: "select",
            isMulti: true,
            options: PRODUCT_USAGE_FILTER_OPTIONS,
          },
          conditionMap: [{ condition: "productUsageIncludes" }],
          defaultCondition: "productUsageIncludes",
        },
        status: {
          defaultCondition: "equals",
          filterMode: "advanced",
          inputConfig: {
            type: "select",
            isMulti: true,
            options: STATUS_FILTER_OPTIONS,
          },
          conditionMap: [
            { condition: "equals" },
            { condition: "notEqual" },
            { condition: "empty", isSeparated: true },
            { condition: "notEmpty" },
          ],
        },
        [UPDATED_AT_COLUMN_KEY]: {
          inputConfig: {
            type: "date",
          },
        },
      },
    },
    selectColumnConfig: {
      selectionMode: "multi",
    },
    editCellConfig: {
      enableColumnEdit: canEdit,
      canEditRow: () => canEdit && selectedRowCountRef.current <= 1,
      onEditCompleted: handleEditCompleted,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 25, 50, 100],
    },
  });

  useEffect(() => {
    if (typeof dataTableProps.setData === "function") {
      dataTableProps.setData(tableRows, true);
    }
  }, [tableRows, dataTableProps.setData]);

  const selectedRows = dataTableProps.rowSelectionState.getSelectedRows();
  const selectedRowIds = selectedRows.map((row) =>
    String(row?.[LAB_DOMAIN_ROW_ID] ?? row?._id ?? "")
  );
  const selectedRowIdsSignature = selectedRowIds.join("|");

  selectedRowCountRef.current = selectedRows.length;
  const selectedRowCount = selectedRowCountRef.current;
  const hasColumnFilters = columnFilters.length > 0;
  const downloadDropdownItems = createDownloadDropdownItems();
  const rowsInEditMode = dataTableProps.editRowState?.rowsInEditMode || {};
  const hasInlineEditsInProgress = Object.values(rowsInEditMode).some(Boolean);
  const unsavedNavigationMessage =
    "You have unsaved inline edits. If you leave this page, those edits will be lost.";

  useDefaultAdvancedFilterInput();

  const navigationContextValue = useMemo(
    () => ({
      getReturnState,
      handleCodeEvidenceNavigation: (target) => {
        if (hasInlineEditsInProgress) {
          setPendingCodeEvidenceNavigation(target);
          return false;
        }

        saveLabMvdReturnState(target.state?.labMvdReturnState);
        return true;
      },
    }),
    [getReturnState, hasInlineEditsInProgress]
  );

  useEffect(() => {
    onSelectedRowsChange?.(selectedRows);
    onEditRowsButtonDisabledChange?.(!canEdit || selectedRows.length < 1);
  }, [canEdit, selectedRowIdsSignature]);

  useEffect(() => {
    onTableStateChange?.({ sorting, columnFilters, pagination });
  }, [sorting, columnFilters, pagination, onTableStateChange]);

  useEffect(() => {
    onInlineEditingChange?.(hasInlineEditsInProgress);
  }, [hasInlineEditsInProgress, onInlineEditingChange]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasInlineEditsInProgress) {
        return undefined;
      }

      event.preventDefault();
      event.returnValue = unsavedNavigationMessage;
      return unsavedNavigationMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasInlineEditsInProgress]);

  const closePendingCodeEvidenceNavigation = () => {
    setPendingCodeEvidenceNavigation(null);
  };

  const confirmPendingCodeEvidenceNavigation = () => {
    const target = pendingCodeEvidenceNavigation;
    setPendingCodeEvidenceNavigation(null);
    if (!target) return;

    saveLabMvdReturnState(target.state?.labMvdReturnState);
    allowPendingNavigationRef.current = true;
    history.push(target);
  };

  return (
    <LabCodeEvidenceNavigationContext.Provider value={navigationContextValue}>
      <LoincValuesContext.Provider value={loincValuesRef}>
        <Prompt
          when={hasInlineEditsInProgress}
          message={() =>
            allowPendingNavigationRef.current || unsavedNavigationMessage
          }
        />
        <div className="mvd-data-table" style={TABLE_CONTAINER_STYLE}>
          <div style={DISCLAIMER_STYLE}>
            <span style={DISCLAIMER_TITLE_STYLE}>
              AI-Generated Mapping Recommendation
            </span>
            This recommendation was produced using AI/ML models based on
            available mapping evidence and historical data. Recommendations must
            be reviewed and approved by an authorized user before being used in
            production.
          </div>
          <MvdClearFiltersToolbar
            hasColumnFilters={hasColumnFilters}
            onClearFilters={() => handleColumnFiltersChange([])}
          />
          <DataTable hideHeader tableState={dataTableProps}>
            <DataTable.SlotWrapper>
              <DataTable.DownloadDropdown
                dropdownMenuItems={downloadDropdownItems}
              />
            </DataTable.SlotWrapper>
            <DataTable.SlotWrapper
              name="multi-edit-modal"
              css={{ justifyContent: "flex-end" }}
            >
              {canEdit && selectedRowCount > 1 && (
                <Button aria-haspopup="dialog" onClick={onOpenEditDialog}>
                  Edit Rows
                </Button>
              )}
            </DataTable.SlotWrapper>
            <DataTable.Table scrollableFocus />
            <DataTable.SlotWrapper>
              <DataTable.Pagination showPageSizeDropdown />
            </DataTable.SlotWrapper>
          </DataTable>
          <BulkEditModal
            isOpen={isOpenEditDialog}
            onClose={onCloseEditDialog}
            selectedRows={selectedRows}
            dataTableProps={dataTableProps}
            LAB_DOMAIN_ROW_ID={LAB_DOMAIN_ROW_ID}
            onConfirm={onConfirmEdit}
          />
          <MappingHistoryDrawer
            isOpen={isHistoryDrawerOpen}
            onClose={() => setIsHistoryDrawerOpen(false)}
            row={selectedHistoryRow}
          />
          <ModalDialog
            isOpen={!!pendingCodeEvidenceNavigation}
            header="Unsaved Inline Edits"
            onClose={closePendingCodeEvidenceNavigation}
            footer={
              <AbyssLayout.Group alignLayout="right">
                <Button
                  variant="outline"
                  color="neutral"
                  onClick={closePendingCodeEvidenceNavigation}
                >
                  Cancel
                </Button>
                <Button onClick={confirmPendingCodeEvidenceNavigation}>
                  Continue
                </Button>
              </AbyssLayout.Group>
            }
          >
            {unsavedNavigationMessage}
          </ModalDialog>
        </div>
      </LoincValuesContext.Provider>
    </LabCodeEvidenceNavigationContext.Provider>
  );
}

LabMapValueDefinitionGrid.propTypes = {
  rowData: PropTypes.arrayOf(PropTypes.object),
  loincValues: PropTypes.arrayOf(PropTypes.object),
  isOpenEditDialog: PropTypes.bool,
  onCloseEditDialog: PropTypes.func,
  onConfirmEdit: PropTypes.func.isRequired,
  onOpenEditDialog: PropTypes.func,
  onSelectedRowsChange: PropTypes.func,
  onEditRowsButtonDisabledChange: PropTypes.func,
  onTableStateChange: PropTypes.func,
  onInlineEditingChange: PropTypes.func,
  getReturnState: PropTypes.func,
  initialTableState: PropTypes.shape({
    sorting: PropTypes.array,
    columnFilters: PropTypes.array,
    pagination: PropTypes.object,
  }),
  mapStatus: PropTypes.string,
  isEditableVersion: PropTypes.bool,
};
